// client/src/reservations/components/PhotoLightbox.jsx
import React, { useEffect, useRef, useState } from 'react';

export default function PhotoLightbox({ photos, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const total = photos?.length || 0;

  const containerRef = useRef(null);
  const touch = useRef({ x: 0, y: 0, active: false });

  function next() { setIdx(i => (i + 1) % total); }
  function prev() { setIdx(i => (i - 1 + total) % total); }

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function onTouchStart(e) {
    const t = e.touches?.[0];
    if (!t) return;
    touch.current = { x: t.clientX, y: t.clientY, active: true };
  }
  function onTouchEnd(e) {
    if (!touch.current.active) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touch.current.x;
    touch.current.active = false;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
  }

  if (!total) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-modal="true"
      role="dialog"
    >
      {/* Close */}
      <button
        className="absolute top-4 right-4 text-white/90 hover:text-white text-2xl"
        onClick={onClose}
        aria-label="Close"
      >✕</button>

      {/* Arrows */}
      {total > 1 && (
        <>
          <button
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 w-10 h-10 flex items-center justify-center text-white text-xl"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
          >‹</button>
          <button
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 w-10 h-10 flex items-center justify-center text-white text-xl"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
          >›</button>
        </>
      )}

      {/* Main image */}
      <img
        src={photos[idx]}
        alt=""
        className="max-h-[82vh] max-w-[95vw] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Thumbnail rail (scrollable) */}
      {total > 1 && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-black/70">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {photos.map((src, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`shrink-0 rounded-lg overflow-hidden border ${i === idx ? 'border-white' : 'border-white/20'}`}
                aria-label={`Show photo ${i+1}`}
                title={`Photo ${i+1}`}
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className={`h-16 w-24 object-cover ${i === idx ? '' : 'opacity-80 hover:opacity-100'}`}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
